import { useEffect, useState, useMemo, useCallback } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import {
  Table,
  TableColumn,
  Link,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { Entity, RELATION_PARENT_OF } from '@backstage/catalog-model';
import {
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  makeStyles,
  Typography,
  Tooltip,
} from '@material-ui/core';
import {
  evaluateCompliance,
  worstSeverity,
  issuesForField,
  ComplianceIssue,
  ComplianceSeverity,
} from './awsComplianceConfig';

// ---------------------------------------------------------------------------
// Display fields — driven by geniussports.com/display-fields annotation
// ---------------------------------------------------------------------------

const DISPLAY_VALUE_TRUNCATE = 50;

interface DisplayFieldEntry {
  l: string;
  v: string;
}

function parseDisplayFields(raw: string | undefined): DisplayFieldEntry[] {
  if (!raw) return [];
  try {
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

const COMPLIANCE_LABEL_TO_FIELD: Record<string, string> = {
  Engine: 'engine',
  Class: 'instanceClass',
};

// ---------------------------------------------------------------------------
// Deterministic type colour palette
// ---------------------------------------------------------------------------

const TYPE_PALETTE = [
  '#1565c0',
  '#6a1b9a',
  '#2e7d32',
  '#c62828',
  '#e65100',
  '#00838f',
  '#283593',
  '#558b2f',
  '#ad1457',
  '#4527a0',
  '#00695c',
  '#bf360c',
  '#0277bd',
  '#827717',
  '#6d4c41',
];

function typeColor(type: string): string {
  let hash = 0;
  for (let i = 0; i < type.length; i++) {
    hash = ((hash << 5) - hash + type.charCodeAt(i)) | 0;
  }
  return TYPE_PALETTE[Math.abs(hash) % TYPE_PALETTE.length];
}

function isAwsResource(entity: Entity): boolean {
  return !!entity.metadata.annotations?.['aws.amazon.com/account-id'];
}
// Resource type configuration
//
// To add a new AWS resource type, add an entry here. The component will
// automatically include it in catalog queries, type filters, and render
// the correct "Config" column chips.
// ---------------------------------------------------------------------------

interface ConfigField {
  label: string;
  /** Annotation key to read the value from */
  annotation: string;
  /** Max characters before truncating (shows full value in tooltip) */
  truncate?: number;
  /** Compliance field name — used to pull issues from evaluateCompliance() */
  complianceField?: string;
}

interface ResourceTypeConfig {
  /** Chip colour in the Type column */
  color: string;
  /** Fields shown in the Config column for this resource type */
  configFields: ConfigField[];
}

const RESOURCE_TYPE_CONFIG: Record<string, ResourceTypeConfig> = {
  'rds-instance': {
    color: '#1565c0',
    configFields: [
      {
        label: 'Engine',
        annotation: 'aws.amazon.com/engine-version',
        complianceField: 'engine',
      },
      {
        label: 'Class',
        annotation: 'aws.amazon.com/instance-class',
        complianceField: 'instanceClass',
      },
    ],
  },
  'rds-cluster': {
    color: '#6a1b9a',
    configFields: [
      {
        label: 'Engine',
        annotation: 'aws.amazon.com/engine-version',
        complianceField: 'engine',
      },
    ],
  },
  'acm-certificate': {
    color: '#2e7d32',
    configFields: [
      {
        label: 'Domains',
        annotation: 'aws.amazon.com/acm-domains',
        truncate: 50,
      },
      {
        label: 'Expires',
        annotation: 'aws.amazon.com/acm-not-after',
      },
      {
        label: 'Validation',
        annotation: 'aws.amazon.com/acm-validation-method',
      },
    ],
  },
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles(theme => ({
  filterBar: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  select: {
    minWidth: 200,
  },
  typeChip: {
    fontWeight: 500,
    fontSize: '0.75rem',
  },
  tagChip: {
    margin: 2,
    fontSize: '0.7rem',
  },
  missingTag: {
    margin: 2,
    fontSize: '0.7rem',
    backgroundColor: '#ffcdd2',
    color: '#b71c1c',
  },
  configChip: {
    margin: 2,
    fontSize: '0.7rem',
  },
  complianceWarning: {
    margin: 2,
    fontSize: '0.7rem',
    backgroundColor: '#fff3e0',
    color: '#e65100',
    cursor: 'default',
  },
  complianceCritical: {
    margin: 2,
    fontSize: '0.7rem',
    backgroundColor: '#ffcdd2',
    color: '#b71c1c',
    cursor: 'default',
  },
  configCell: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    alignItems: 'center',
  },
}));

// ---------------------------------------------------------------------------
// Row type
// ---------------------------------------------------------------------------

interface CostInfo {
  month: string;
  total: number;
  breakdown: Record<string, number>;
}

interface AwsResourceRow {
  name: string;
  title: string;
  type: string;
  owner: string;
  namespace: string;
  account: string;
  region: string;
  billingTeamName: string;
  entity: Entity;
  complianceIssues: ComplianceIssue[];
  complianceSeverity: ComplianceSeverity | null;
  latestCost: CostInfo | null;
}

// ---------------------------------------------------------------------------
// Helpers — org-team tree & subgroup collection
// ---------------------------------------------------------------------------

interface OrgTeamInfo {
  name: string;
  title: string;
  billingTeamNames: string[];
}

async function collectSubgroupNames(
  catalogApi: { getEntities: Function },
  groupName: string,
  groupNamespace: string,
): Promise<string[]> {
  const { items } = await catalogApi.getEntities({
    filter: { kind: 'Group' },
    fields: ['kind', 'metadata.name', 'metadata.namespace', 'relations'],
  });

  const childrenOf = new Map<string, string[]>();
  for (const entity of items) {
    const ns = (entity as any).metadata.namespace ?? 'default';
    const name = (entity as any).metadata.name;
    const ref = `group:${ns}/${name}`;
    const rels = (entity as any).relations ?? [];
    for (const rel of rels) {
      if (rel.type === RELATION_PARENT_OF && rel.targetRef) {
        if (!childrenOf.has(ref)) childrenOf.set(ref, []);
        childrenOf.get(ref)!.push(rel.targetRef);
      }
    }
  }

  const result: string[] = [];
  const visited = new Set<string>();
  const queue = [`group:${groupNamespace}/${groupName}`];
  while (queue.length > 0) {
    const ref = queue.shift()!;
    if (visited.has(ref)) continue;
    visited.add(ref);
    const refName =
      ref
        .replace(/^group:/, '')
        .split('/')
        .pop() ?? '';
    if (refName.toLowerCase() === 'unknown') continue;
    result.push(ref);
    for (const child of childrenOf.get(ref) ?? []) {
      queue.push(child);
    }
  }

  return result;
}

function parseAnnotationList(entity: Entity, key: string): string[] {
  const val = entity.metadata.annotations?.[key];
  if (!val) return [];
  return val
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
}

async function collectOrgTeamTree(
  catalogApi: { getEntities: Function },
  rootName: string,
): Promise<OrgTeamInfo[]> {
  const { items } = await catalogApi.getEntities({
    filter: {
      kind: 'Group',
      'metadata.annotations.geniussports.com/org-team': 'true',
    },
  });

  const byName = new Map<string, Entity>();
  for (const e of items) byName.set(e.metadata.name, e);

  const result: OrgTeamInfo[] = [];
  const visited = new Set<string>();
  const queue = [rootName];

  while (queue.length > 0) {
    const name = queue.shift()!;
    if (visited.has(name)) continue;
    visited.add(name);

    const entity = byName.get(name);
    if (!entity) continue;

    const billingNames = parseAnnotationList(
      entity,
      'geniussports.com/aws-billing-team-names',
    );
    const spec = (entity as any).spec ?? {};
    result.push({
      name,
      title:
        (entity.metadata as any).title ?? spec.profile?.displayName ?? name,
      billingTeamNames: billingNames,
    });

    const children = parseAnnotationList(
      entity,
      'geniussports.com/org-team-children',
    );
    for (const child of children) queue.push(child);
  }

  return result;
}

// ---------------------------------------------------------------------------
// Cost parsing
// ---------------------------------------------------------------------------

function parseLatestCost(raw: string | undefined): CostInfo | null {
  if (!raw) return null;
  try {
    const history: Record<string, Record<string, number>> = JSON.parse(raw);
    const months = Object.keys(history).sort();
    if (months.length === 0) return null;
    const month = months[months.length - 1];
    const breakdown = history[month];
    const total = Object.values(breakdown).reduce((s, v) => s + v, 0);
    return { month, total, breakdown };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Row builder
// ---------------------------------------------------------------------------

function toRows(items: Entity[]): AwsResourceRow[] {
  return items.map((e: Entity) => {
    const spec = (e as any).spec ?? {};
    const annotations = e.metadata.annotations ?? {};

    const engineVersion = annotations['aws.amazon.com/engine-version'] ?? '';
    const instanceClass = annotations['aws.amazon.com/instance-class'] ?? '';
    const entityTags: string[] = (e.metadata as any).tags ?? [];
    const complianceIssues = evaluateCompliance(
      engineVersion,
      instanceClass,
      entityTags,
      spec.type ?? '',
    );

    return {
      name: e.metadata.name,
      title: (e.metadata as any).title ?? e.metadata.name,
      type: spec.type ?? '',
      owner: spec.owner ?? '',
      namespace: e.metadata.namespace ?? 'default',
      account: annotations['aws.amazon.com/account-id'] ?? '',
      region: annotations['aws.amazon.com/region'] ?? '',
      billingTeamName: annotations['geniussports.com/billing-team-name'] ?? '',
      entity: e,
      complianceIssues,
      complianceSeverity: worstSeverity(complianceIssues),
      latestCost: parseLatestCost(annotations['geniussports.com/cost-history']),
    };
  });
}

// ---------------------------------------------------------------------------
// Config column renderer
// ---------------------------------------------------------------------------

function ConfigCell({
  row,
  classes,
}: {
  row: AwsResourceRow;
  classes: ReturnType<typeof useStyles>;
}) {
  const annotations = row.entity.metadata.annotations ?? {};
  const displayFields = parseDisplayFields(
    annotations['geniussports.com/display-fields'],
  );

  if (displayFields.length > 0) {
    return (
      <div className={classes.configCell}>
        {displayFields.map((f, idx) => {
          const complianceKey = COMPLIANCE_LABEL_TO_FIELD[f.l];
          const issues = complianceKey
            ? issuesForField(row.complianceIssues, complianceKey as any)
            : [];
          const severity = worstSeverity(issues);

          const truncated =
            f.v.length > DISPLAY_VALUE_TRUNCATE
              ? `${f.v.slice(0, DISPLAY_VALUE_TRUNCATE)}…`
              : f.v;
          const chipLabel = `${f.l}: ${truncated}`;

          if (severity) {
            const tooltip = issues.map(i => i.message).join('\n');
            return (
              <Tooltip key={`df-${idx}`} title={tooltip}>
                <Chip
                  label={chipLabel}
                  size="small"
                  className={
                    severity === 'critical'
                      ? classes.complianceCritical
                      : classes.complianceWarning
                  }
                />
              </Tooltip>
            );
          }

          if (f.v.length > DISPLAY_VALUE_TRUNCATE) {
            return (
              <Tooltip key={`df-${idx}`} title={f.v}>
                <Chip
                  label={chipLabel}
                  size="small"
                  className={classes.configChip}
                />
              </Tooltip>
            );
          }

          return (
            <Chip
              key={`df-${idx}`}
              label={chipLabel}
              size="small"
              className={classes.configChip}
            />
          );
        })}
      </div>
    );
  }

  const cfg = RESOURCE_TYPE_CONFIG[row.type];
  if (!cfg) return <>—</>;

  const chips = cfg.configFields.map(field => {
    const raw = annotations[field.annotation] ?? '';
    if (!raw) return null;

    const issues = field.complianceField
      ? issuesForField(row.complianceIssues, field.complianceField as any)
      : [];
    const severity = worstSeverity(issues);

    let displayValue = raw;
    if (field.truncate && raw.length > field.truncate) {
      displayValue = `${raw.slice(0, field.truncate)}…`;
    }

    const chipLabel = `${field.label}: ${displayValue}`;

    if (severity) {
      const tooltip = issues.map(i => i.message).join('\n');
      return (
        <Tooltip key={field.annotation} title={tooltip}>
          <Chip
            label={chipLabel}
            size="small"
            className={
              severity === 'critical'
                ? classes.complianceCritical
                : classes.complianceWarning
            }
          />
        </Tooltip>
      );
    }

    if (field.truncate && raw.length > field.truncate) {
      return (
        <Tooltip key={field.annotation} title={raw}>
          <Chip label={chipLabel} size="small" className={classes.configChip} />
        </Tooltip>
      );
    }

    return (
      <Chip
        key={field.annotation}
        label={chipLabel}
        size="small"
        className={classes.configChip}
      />
    );
  });

  const nonNull = chips.filter(Boolean) as JSX.Element[];
  if (nonNull.length === 0) return <>—</>;

  return <div className={classes.configCell}>{nonNull}</div>;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const GroupAwsResourcesContent = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const { entity } = useEntity();

  const groupName = entity.metadata.name;
  const groupNamespace = entity.metadata.namespace ?? 'default';
  const isOrgTeam =
    entity.metadata.annotations?.['geniussports.com/org-team'] === 'true';

  const [resources, setResources] = useState<AwsResourceRow[]>([]);
  const [subgroupRefs, setSubgroupRefs] = useState<string[]>([]);
  const [orgTeamTree, setOrgTeamTree] = useState<OrgTeamInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedCompliance, setSelectedCompliance] = useState<string>('');

  const load = useCallback(async () => {
    try {
      setLoading(true);

      const { items: allResources } = await catalogApi.getEntities({
        filter: { kind: 'Resource' },
      });
      const items = allResources.filter(isAwsResource);

      if (isOrgTeam) {
        const tree = await collectOrgTeamTree(catalogApi, groupName);
        setOrgTeamTree(tree);

        const allBillingNames = new Set(
          tree.flatMap(t => t.billingTeamNames.map(n => n.toLowerCase())),
        );

        const matched = items.filter((e: Entity) => {
          const ann = e.metadata.annotations ?? {};
          const billing = (
            ann['geniussports.com/billing-team-name'] ?? ''
          ).toLowerCase();
          return billing && allBillingNames.has(billing);
        });

        setResources(toRows(matched));
      } else {
        const refs = await collectSubgroupNames(
          catalogApi,
          groupName,
          groupNamespace,
        );
        setSubgroupRefs(refs);

        const ownerNames = refs.map(r => {
          const parts = r.replace(/^group:/, '').split('/');
          return parts.length > 1 ? parts[1] : parts[0];
        });

        const ownerSet = new Set(
          ownerNames.map(n => n.toLowerCase()).filter(n => n !== 'unknown'),
        );
        const matched = items.filter((e: Entity) => {
          const owner = ((e as any).spec?.owner ?? '').toLowerCase();
          return owner !== 'unknown' && ownerSet.has(owner);
        });

        setResources(toRows(matched));
      }

      setError(undefined);
    } catch (e: any) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [catalogApi, groupName, groupNamespace, isOrgTeam]);

  useEffect(() => {
    load();
  }, [load]);

  const teamFilterOptions = useMemo(() => {
    if (!isOrgTeam) {
      const set = new Set(resources.map(r => r.owner));
      return Array.from(set)
        .sort()
        .map(o => ({ value: o, label: o.split('/').pop() ?? o }));
    }
    return orgTeamTree
      .filter(t => t.billingTeamNames.length > 0)
      .sort((a, b) => a.title.localeCompare(b.title))
      .map(t => ({ value: t.name, label: t.title }));
  }, [isOrgTeam, resources, orgTeamTree]);

  const typeOptions = useMemo(() => {
    const set = new Set(resources.map(r => r.type));
    return Array.from(set).sort();
  }, [resources]);

  const billingNamesByTeam = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const t of orgTeamTree) {
      map.set(t.name, new Set(t.billingTeamNames.map(n => n.toLowerCase())));
    }
    return map;
  }, [orgTeamTree]);

  const filtered = useMemo(
    () =>
      resources.filter(r => {
        if (selectedTeam) {
          if (isOrgTeam) {
            const names = billingNamesByTeam.get(selectedTeam);
            const billing = (
              r.entity.metadata.annotations?.[
                'geniussports.com/billing-team-name'
              ] ?? ''
            ).toLowerCase();
            if (!names || !names.has(billing)) return false;
          } else {
            if (r.owner !== selectedTeam) return false;
          }
        }
        if (selectedType && r.type !== selectedType) return false;
        if (selectedCompliance === 'ok' && r.complianceSeverity !== null)
          return false;
        if (
          selectedCompliance === 'warning' &&
          r.complianceSeverity !== 'warning'
        )
          return false;
        if (
          selectedCompliance === 'critical' &&
          r.complianceSeverity !== 'critical'
        )
          return false;
        if (selectedCompliance === 'any-issue' && r.complianceSeverity === null)
          return false;
        return true;
      }),
    [
      resources,
      selectedTeam,
      selectedType,
      selectedCompliance,
      isOrgTeam,
      billingNamesByTeam,
    ],
  );

  if (loading) return <Progress />;
  if (error) return <ResponseErrorPanel error={error} />;

  const columns: TableColumn<AwsResourceRow>[] = [
    {
      title: 'Name',
      field: 'title',
      highlight: true,
      render: row => (
        <Link to={`/catalog/${row.namespace}/resource/${row.name}`}>
          {row.title}
        </Link>
      ),
    },
    {
      title: 'Type',
      field: 'type',
      render: row => (
        <Chip
          label={row.type}
          size="small"
          className={classes.typeChip}
          style={{
            backgroundColor:
              RESOURCE_TYPE_CONFIG[row.type]?.color ?? typeColor(row.type),
            color: '#fff',
          }}
        />
      ),
    },
    {
      title: 'Owner',
      field: 'owner',
      render: row => row.owner.split('/').pop() ?? row.owner,
    },
    {
      title: 'Account',
      field: 'account',
    },
    {
      title: 'Region',
      field: 'region',
    },
    {
      title: 'Config',
      sorting: false,
      render: row => <ConfigCell row={row} classes={classes} />,
    },
    {
      title: 'Cost last month',
      customSort: (a, b) =>
        (a.latestCost?.total ?? 0) - (b.latestCost?.total ?? 0),
      defaultSort: 'desc' as const,
      render: (row: AwsResourceRow) => {
        if (!row.latestCost)
          return (
            <Typography variant="body2" color="textSecondary">
              —
            </Typography>
          );
        const { month, total, breakdown } = row.latestCost;
        const tip = [
          `${month}`,
          ...Object.entries(breakdown).map(
            ([k, v]) => `  ${k}: $${v.toFixed(2)}`,
          ),
        ].join('\n');
        return (
          <Tooltip
            title={<span style={{ whiteSpace: 'pre-line' }}>{tip}</span>}
          >
            <Typography
              variant="body2"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              ${total.toFixed(2)}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      title: 'Billing Team',
      field: 'billingTeamName',
      render: row =>
        row.billingTeamName ? (
          <Chip
            label={row.billingTeamName}
            size="small"
            className={classes.tagChip}
          />
        ) : (
          <Chip label="MISSING" size="small" className={classes.missingTag} />
        ),
    },
  ];

  return (
    <>
      <Box className={classes.filterBar}>
        <FormControl variant="outlined" size="small" className={classes.select}>
          <InputLabel>{isOrgTeam ? 'Team' : 'Subgroup'}</InputLabel>
          <Select
            value={selectedTeam}
            onChange={e => setSelectedTeam(e.target.value as string)}
            label={isOrgTeam ? 'Team' : 'Subgroup'}
          >
            <MenuItem value="">All ({resources.length})</MenuItem>
            {teamFilterOptions.map(o => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" className={classes.select}>
          <InputLabel>Resource Type</InputLabel>
          <Select
            value={selectedType}
            onChange={e => setSelectedType(e.target.value as string)}
            label="Resource Type"
          >
            <MenuItem value="">All Types</MenuItem>
            {typeOptions.map(t => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" className={classes.select}>
          <InputLabel>Compliance</InputLabel>
          <Select
            value={selectedCompliance}
            onChange={e => setSelectedCompliance(e.target.value as string)}
            label="Compliance"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="ok">OK only</MenuItem>
            <MenuItem value="any-issue">Has issues</MenuItem>
            <MenuItem value="warning">Warning</MenuItem>
            <MenuItem value="critical">Critical</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" color="textSecondary">
          {isOrgTeam ? (
            <>
              Showing resources for{' '}
              <strong>{(entity.metadata as any).title ?? groupName}</strong>
              {orgTeamTree.length > 1 &&
                ` and ${orgTeamTree.length - 1} sub-team(s)`}
            </>
          ) : (
            <>
              Showing resources owned by <strong>{groupName}</strong>
              {subgroupRefs.length > 1 &&
                ` and ${subgroupRefs.length - 1} subgroup(s)`}
            </>
          )}
        </Typography>
      </Box>
      <Table
        title={`AWS Resources (${filtered.length})`}
        columns={columns}
        data={filtered}
        options={{
          search: true,
          paging: true,
          pageSize: 25,
          pageSizeOptions: [25, 50, 100],
          padding: 'dense',
        }}
      />
    </>
  );
};
